import {AlertCircleIcon, ArrowDownIcon, ArrowUpIcon, Loader2Icon} from "lucide-react";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import {Card, CardContent} from "@components/ui/card";
import {
    type CEOSummaryV2Card,
    type CEOSummaryV2Opts,
    type CEOSummaryV2Scorecard,
    type CEOSummaryV2Trend,
    useVNextCEOSummaryV2,
} from "@hooks/use-vnext";

import {ExecutiveSummary} from "./ExecutiveSummary";

// ── Formatters ───────────────────────────────────────────────────

function fmtCompact(v: number | null): string {
    if (v == null) return "—";
    const abs = Math.abs(v);
    const sign = v < 0 ? "-" : "";
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
    return `${sign}$${abs.toFixed(0)}`;
}

function fmtPercent(v: number | null): string {
    if (v == null) return "—";
    return `${v.toFixed(1)}%`;
}

function fmtCardValue(v: number | null, format: "currency" | "percent"): string {
    return format === "currency" ? fmtCompact(v) : fmtPercent(v);
}

function fmtScorecardValue(v: number | null, format: "currency" | "percent"): string {
    if (v == null) return "—";
    if (format === "percent") return fmtPercent(v);
    const abs = Math.abs(v);
    const sign = v < 0 ? "-" : "";
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
    return `${sign}$${abs.toFixed(0)}`;
}

function fmtTooltipCurrency(v: number): string {
    const abs = Math.abs(v);
    const formatted = abs.toLocaleString("en-AU", {minimumFractionDigits: 0, maximumFractionDigits: 0});
    return v < 0 ? `-$${formatted}` : `$${formatted}`;
}

function parseMonthLabel(monthKey: string): string {
    const d = new Date(monthKey + "-01");
    return d.toLocaleString("en-AU", {month: "short", year: "numeric"});
}

function parseShortMonth(monthKey: string): string {
    const d = new Date(monthKey + "-01");
    return d.toLocaleString("en-AU", {month: "short"});
}

// ── KPI Card ─────────────────────────────────────────────────────

const UP_IS_BAD = new Set([
    "operating_expenses", "opex", "cogs", "cost_of_sales",
    "total_expenses", "ar_overdue", "overdue", "total_liabilities",
]);

function isUpBad(key: string): boolean {
    if (UP_IS_BAD.has(key)) return true;
    if (key.includes("expense") || key.includes("cost") || key.includes("cogs") || key.includes("liabilit")) return true;
    return false;
}

function KPICard({card, periodMode}: {card: CEOSummaryV2Card; periodMode: string}) {
    const isPeriodTotal = periodMode === "YTD" || periodMode === "QTD" || periodMode === "FY";
    const primaryValue = isPeriodTotal ? card.ytd : card.mtd;

    const isNetCash = card.key === "net_cash_movement";
    const primaryColor = isNetCash && primaryValue != null
        ? primaryValue > 0 ? "text-green-600" : primaryValue < 0 ? "text-red-600" : "text-[#209DB3]"
        : "text-[#209DB3]";

    const pct = card.ytd_vs_py_pct;
    const inverted = isUpBad(card.key);
    const isPositive = pct != null && pct >= 0;
    const isGood = inverted ? !isPositive : isPositive;
    const changeColor = pct != null ? (isGood ? "text-green-600" : "text-red-600") : "";

    return (
        <Card>
            <CardContent className="p-5 text-center space-y-1">
                <p className={`text-2xl font-extrabold tabular-nums ${primaryColor}`}>
                    {fmtCardValue(primaryValue, card.format)}
                </p>
                <p className="text-sm font-bold leading-snug text-[#0F172A]">{card.label}</p>
                <div className="flex items-center justify-center gap-3 text-xs mt-2">
                    {isPeriodTotal ? (
                        <span className="text-muted-foreground">PY {fmtCardValue(card.py_ytd, card.format)}</span>
                    ) : (
                        <>
                            <span className="text-muted-foreground">YTD {fmtCardValue(card.ytd, card.format)}</span>
                            <span className="text-muted-foreground">PY {fmtCardValue(card.py_ytd, card.format)}</span>
                        </>
                    )}
                </div>
                {pct != null && (
                    <div className="flex items-center justify-center gap-1 mt-1">
                        {isPositive
                            ? <ArrowUpIcon className={`size-3 ${changeColor}`} />
                            : <ArrowDownIcon className={`size-3 ${changeColor}`} />}
                        <span className={`text-xs font-medium ${changeColor}`}>
                            {Math.abs(pct).toFixed(1)}% vs PY
                        </span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ── Monthly Scorecard Table ──────────────────────────────────────

const SCORECARD_COLS: {key: keyof CEOSummaryV2Scorecard; label: string; format: "currency" | "percent"}[] = [
    {key: "revenue", label: "Revenue", format: "currency"},
    {key: "cogs", label: "COGS", format: "currency"},
    {key: "gross_profit", label: "Gross Profit", format: "currency"},
    {key: "gross_margin_pct", label: "GP %", format: "percent"},
    {key: "opex", label: "OpEx", format: "currency"},
    {key: "net_profit", label: "Net Profit", format: "currency"},
    {key: "net_margin_pct", label: "NP %", format: "percent"},
    {key: "closing_cash", label: "Net Cash", format: "currency"},
    {key: "net_cash_movement", label: "Net Cash Mvmt", format: "currency"},
];

function ScorecardTable({rows}: {rows: CEOSummaryV2Scorecard[]}) {
    if (rows.length === 0) return null;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
                <thead>
                    <tr className="border-b">
                        <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Metric</th>
                        {rows.map((r) => (
                            <th key={r.month_key} className="text-right py-2 px-2 font-medium text-muted-foreground min-w-[80px]">
                                {parseMonthLabel(r.month_key)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {SCORECARD_COLS.map((col, ci) => (
                        <tr
                            key={col.key}
                            className={`border-b last:border-b-0 ${ci % 2 === 0 ? "bg-muted/30" : ""}`}
                        >
                            <td className="py-1.5 pr-3 font-medium text-muted-foreground whitespace-nowrap">
                                {col.label}
                            </td>
                            {rows.map((r) => {
                                const val = r[col.key] as number;
                                const isNeg = val != null && val < 0;
                                return (
                                    <td
                                        key={r.month_key}
                                        className={`py-1.5 px-2 text-right tabular-nums ${isNeg ? "text-red-600" : ""}`}
                                    >
                                        {fmtScorecardValue(val, col.format)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── 12-Month Trend Chart ─────────────────────────────────────────

const TREND_COLORS = {
    revenue: "#209DB3",
    net_profit: "#5BF6C8",
    closing_cash: "#6366f1",
};

function TrendChart({rows}: {rows: CEOSummaryV2Trend[]}) {
    if (rows.length === 0) return null;

    const chartData = rows.map((r) => ({
        month: parseShortMonth(r.month_key),
        Revenue: r.revenue,
        "Net Profit": r.net_profit,
        "Net Cash Position": r.closing_cash,
    }));

    return (
        <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{top: 5, right: 20, left: 10, bottom: 5}}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="month" tick={{fontSize: 11}} />
                    <YAxis
                        tick={{fontSize: 11}}
                        tickFormatter={(v: number) => fmtCompact(v)}
                        width={65}
                    />
                    <Tooltip
                        formatter={(value: number) => fmtTooltipCurrency(value)}
                        labelFormatter={(label: string) => label}
                        contentStyle={{fontSize: 12}}
                    />
                    <Legend wrapperStyle={{fontSize: 12}} />
                    <Line type="monotone" dataKey="Revenue" stroke={TREND_COLORS.revenue} strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} />
                    <Line type="monotone" dataKey="Net Profit" stroke={TREND_COLORS.net_profit} strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} />
                    <Line type="monotone" dataKey="Net Cash Position" stroke={TREND_COLORS.closing_cash} strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

// ── Main Component ───────────────────────────────────────────────

interface CEOSummaryV2TabProps {
    workspaceId: string;
    scenarioId?: string;
    period?: string;
    selectedMonth?: string;
    currentMonth?: string;
    fyStartMonth?: number;
    fyEndMonth?: number;
    customFrom?: string;
    customTo?: string;
}

const PERIOD_LABELS: Record<string, string> = {
    MTD: "Month to Date",
    QTD: "Quarter to Date",
    YTD: "Year to Date",
    FY: "Full Year",
    MONTH: "Full Month",
};

export function CEOSummaryV2Tab({workspaceId, scenarioId, period, selectedMonth, currentMonth, fyStartMonth, fyEndMonth, customFrom, customTo}: CEOSummaryV2TabProps) {
    const opts: CEOSummaryV2Opts = {period, selectedMonth, currentMonth, fyStartMonth, customFrom, customTo};
    const {data, isLoading, error} = useVNextCEOSummaryV2(workspaceId, opts);

    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading CEO Summary...</span>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex h-64 items-center justify-center">
                <AlertCircleIcon className="size-5 text-red-500" />
                <span className="ml-2 text-sm text-red-600">{error || "Failed to load CEO summary data."}</span>
            </div>
        );
    }

    const currentMonthLabel = parseMonthLabel(data.period.current_month);
    const fyStart = data.period.fy_start_month;
    const currentDate = new Date(data.period.current_month + "-01");
    const fyYear =
        fyStart <= 1
            ? currentDate.getFullYear()
            : currentDate.getMonth() + 1 >= fyStart
              ? currentDate.getFullYear() + 1
              : currentDate.getFullYear();

    return (
        <div className="space-y-6 p-4" data-tour="dashboard-ceo-tab">
            {/* Header */}
            <div>
                <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3] mb-1">CEO Summary</h3>
                <p className="text-sm text-muted-foreground">
                    FY{fyYear} — {currentMonthLabel} — {PERIOD_LABELS[data.period.period_mode] ?? data.period.period_mode}
                    {data.period.period_label ? ` (${data.period.period_label})` : ""}
                    {data.period.months_with_data != null && data.period.months_total != null && data.period.months_with_data < data.period.months_total
                        ? ` — ${data.period.months_with_data}/${data.period.months_total} months with data`
                        : ""}
                </p>
                {data.period.data_note && (
                    <p className="text-xs text-amber-600 mt-1">{data.period.data_note}</p>
                )}
            </div>

            {/* Executive Summary (LLM-generated) */}
            <ExecutiveSummary
                workspaceId={workspaceId}
                scenarioId={scenarioId ?? ""}
                tabKey="ceo_summary"
                period={period}
                selectedMonth={selectedMonth}
                currentMonth={currentMonth}
                customFrom={customFrom}
                customTo={customTo}
                fyStartMonth={fyStartMonth}
                fyEndMonth={fyEndMonth}
            />

            {/* KPI Cards Grid */}
            <div data-tour="dashboard-ceo-metrics">
                <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3] mb-3">Executive Snapshot</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {data.cards.map((card) => (
                        <KPICard key={card.key} card={card} periodMode={data.period.period_mode} />
                    ))}
                </div>
            </div>

            {/* Monthly Scorecard */}
            <div className="space-y-2">
                <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Monthly Scorecard</h3>
                <ScorecardTable rows={data.scorecard} />
            </div>

            {/* 12-Month Trend */}
            <div className="space-y-2">
                <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">12-Month Trend</h3>
                <TrendChart rows={data.trend} />
            </div>
        </div>
    );
}

export default CEOSummaryV2Tab;
